import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type ReactNode
} from "react";

/**
 * Modal (Design.md §9): overlay + `--shadow-lg`, fecha no backdrop, no × e em
 * Esc. Usado no grupo de embalagem e no modelo de embalagem (§6.1/§6.2).
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number | string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 560
}: ModalProps): ReactNode {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Move o foco para o diálogo ao abrir (WCAG 2.4.3 — Focus Order).
  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const dialogStyle: CSSProperties = {
    width,
    maxWidth: "calc(100vw - 2rem)",
    maxHeight: "calc(100vh - 2rem)",
    overflow: "auto",
    background: "var(--panel)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    boxShadow: "var(--shadow-lg)",
    padding: "1.1rem 1.25rem",
    outline: "none"
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(7, 18, 29, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1000
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal
        aria-labelledby={title !== undefined ? titleId : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={dialogStyle}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            marginBottom: "0.85rem"
          }}
        >
          <div id={titleId} style={{ fontSize: 15, fontWeight: 700 }}>
            {title}
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-muted)",
              fontSize: 18,
              lineHeight: 1,
              padding: "0.2rem 0.4rem",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </header>
        <div>{children}</div>
        {footer !== undefined ? (
          <footer
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              marginTop: "1rem"
            }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
